import React from 'react';
import { Users, User, Search } from 'lucide-react';

// ============================================================================
// EMPLOYE SELECTOR — Composant partagé
// ============================================================================
// Utilisé par : PaieModule, AvancesModule (et futurs modules)
//
// Props :
//   employes         - Array des employés à afficher
//   selectedEmploye  - ID de l'employé sélectionné (string)
//   onSelect         - Callback quand on sélectionne un employé (id: string)
//   searchTerm       - Valeur du champ recherche
//   onSearchChange   - Callback quand on tape dans la recherche
//   loading          - Boolean, true pendant le chargement
//   color            - Couleur du thème ('primary' | 'accent')
//   renderBadge      - Fonction optionnelle (employe) => JSX pour badge custom
// ============================================================================

const THEMES = {
  primary: {
    headerBg: '#88b9c5',
    headerText: '#ffffff',
    avatarBg: '#88b9c5',
    avatarText: '#ffffff',
    selectedBorder: '#88b9c5',
    selectedBg: '#f0f9fb',
    focusBorder: '#88b9c5',
    focusShadow: 'rgba(136, 185, 197, 0.1)',
  },
  accent: {
    headerBg: '#fed774',
    headerText: '#1f2937',
    avatarBg: '#fed774',
    avatarText: '#1f2937',
    selectedBorder: '#fed774',
    selectedBg: '#fffbeb',
    focusBorder: '#fed774',
    focusShadow: 'rgba(254, 215, 116, 0.1)',
  },
};

const EmployeSelector = ({
  employes = [],
  selectedEmploye = '',
  onSelect,
  searchTerm = '',
  onSearchChange,
  loading = false,
  color = 'primary',
  renderBadge = null,
}) => {
  const theme = THEMES[color] || THEMES.primary;

  // Filtrer les employés par recherche
  const filteredEmployes = employes.filter(e =>
    `${e.prenom} ${e.nom}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      {/* Header */}
      <div
        className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3"
        style={{ backgroundColor: theme.headerBg }}
      >
        <Users size={24} style={{ color: theme.headerText }} />
        <h2 className="font-bold text-lg" style={{ color: theme.headerText }}>
          Personnel
        </h2>
      </div>

      {/* Recherche */}
      <div className="mb-4">
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={16}
          />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-3 py-3 border border-gray-200 rounded-xl focus:outline-none font-medium text-sm transition-all"
            onFocus={(e) => {
              e.target.style.borderColor = theme.focusBorder;
              e.target.style.boxShadow = `0 0 0 3px ${theme.focusShadow}`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e5e5';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {/* Liste des employés */}
      <div className="space-y-3 max-h-[600px] overflow-y-auto">
        {loading ? null : filteredEmployes.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-xs font-medium">Aucun employé</p>
          </div>
        ) : (
          filteredEmployes.map((employe) => {
            const isSelected = selectedEmploye === employe.id.toString();

            return (
              <div
                key={employe.id}
                onClick={() => onSelect(employe)}
                className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                  isSelected
                    ? 'shadow-sm'
                    : 'border-transparent bg-white hover:bg-gray-50'
                }`}
                style={isSelected ? {
                  borderColor: theme.selectedBorder,
                  backgroundColor: theme.selectedBg,
                } : {}}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: theme.avatarBg }}
                  >
                    <User style={{ color: theme.avatarText }} size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 truncate">
                      {employe.prenom} {employe.nom}
                    </div>
                    <div className="text-xs font-medium text-gray-600">
                      {employe.role}
                    </div>
                  </div>
                  {/* Badge custom (ex: "Payé" dans PaieModule) */}
                  {renderBadge && renderBadge(employe)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EmployeSelector;